import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Download, FileSignature, Lock, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SECTION_TITLES = {
  "SEKTION_1_GRUND": "Sektion 1: Reko Grundkontroller (Obligatoriska)",
  "SEKTION_2_RISK": "Sektion 2: Specifika Riskåtgärder (Dynamisk efter Kundprofil)",
};
const CL_STATUS = {
  "EJ_PÅBÖRJAD": "bg-slate-100 text-slate-700",
  "PÅGÅENDE": "bg-amber-100 text-amber-800",
  "AVVIKELSE": "bg-red-100 text-red-800",
  "SLUTFÖRD": "bg-emerald-100 text-emerald-800",
};

export default function Module3({ onReportDeviation, refreshDash }) {
  const { user } = useAuth();
  const isSupervisor = user.role === "Supervisor";
  const [checklists, setChecklists] = useState(null);
  const [active, setActive] = useState(null);
  const [signing, setSigning] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadList = () =>
    api.get("/checklists").then((r) => {
      setChecklists(r.data);
      if (r.data.length && !active) selectChecklist(r.data[0].id);
    }).catch(() => {});

  const selectChecklist = (id) => {
    api.get(`/checklists/${id}`).then((r) => setActive(r.data)).catch(() => {});
    api.get("/attachments", { params: { entity_type: "CHECKLIST", entity_id: id } })
      .then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleItem = async (item, checked) => {
    setActive((a) => ({ ...a, items: a.items.map((i) => (i.id === item.id ? { ...i, is_checked: checked } : i)) }));
    try {
      await api.put(`/checklists/${active.id}/items/${item.id}`, { is_checked: checked });
    } catch (e) {
      toast.error(formatApiError(e));
      selectChecklist(active.id);
    }
  };

  const saveNote = async (item, note) => {
    try {
      await api.put(`/checklists/${active.id}/items/${item.id}`, { note });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (file) => {
    if (!file || !active) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "CHECKLIST");
      fd.append("entity_id", active.id);
      fd.append("file", file);
      await api.post("/attachments/upload", fd);
      toast.success("Bilagan har laddats upp");
      const { data } = await api.get("/attachments", { params: { entity_type: "CHECKLIST", entity_id: active.id } });
      setAttachments(data);
    } catch (e) {
      toast.error(formatApiError(e, "Uppladdningen misslyckades"));
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (att) => {
    try {
      const res = await api.get(`/files/${att.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e, "Nedladdningen misslyckades")); }
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/checklists/${active.id}/sign`);
      toast.success("Uppdraget har signerats och slutförts – loggat i Audit Trail");
      selectChecklist(active.id);
      loadList();
      refreshDash?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSigning(false);
    }
  };

  if (!checklists) return <div className="text-sm text-slate-500">Laddar...</div>;

  const sections = ["SEKTION_1_GRUND", "SEKTION_2_RISK"].map((s) => ({
    key: s, items: (active?.items || []).filter((i) => i.section === s),
  }));
  const isDone = active?.status === "SLUTFÖRD";

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="modul3-view">
      <div className="border-b border-slate-200 pb-4 mb-6">
        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">Modul 3 · Reko 220–225</div>
        <h2 className="text-xl font-extrabold text-slate-900">Riskreducerande Åtgärder & Uppdragschecklista</h2>
        <p className="text-sm text-slate-500 mt-1">Generella och specifika kontroller för att förhindra kvalitetsbrister på byrå- och uppdragsnivå.</p>
      </div>

      <div className="mb-6">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Välj uppdrag:</label>
        <select
          data-testid="checklist-select"
          className="w-full md:w-96 text-sm p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          value={active?.id || ""}
          onChange={(e) => selectChecklist(e.target.value)}
        >
          {checklists.map((c) => (
            <option key={c.id} value={c.id}>{c.client_name} — {c.engagement_type} ({c.status})</option>
          ))}
        </select>
      </div>

      {active && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm gap-4">
            <div>
              <div className="text-xs font-bold text-slate-400 uppercase">Aktivt Uppdrag</div>
              <h3 className="text-lg font-bold text-slate-900" data-testid="active-engagement-title">{active.client_name} — {active.engagement_type}</h3>
              <p className="text-xs text-slate-500">Org.nr: {active.client_org_nr} · Deadline: {active.deadline}</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${CL_STATUS[active.status]}`} data-testid="checklist-status">{active.status}</span>
              {active.risk_level && (
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${active.risk_level === "HÖG" ? "bg-red-100 text-red-800" : active.risk_level === "MEDEL" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                  RISKPROFIL: {active.risk_level}
                </span>
              )}
            </div>
          </div>

          {sections.map((sec, si) => (
            <div key={sec.key} className="bg-white p-5 rounded-lg border border-slate-200 space-y-4">
              <h4 className="font-bold text-sm text-slate-800 border-b border-slate-100 pb-2 flex items-center">
                <span className="bg-blue-600 text-white w-5 h-5 rounded-full inline-flex items-center justify-center text-xs mr-2">{si + 1}</span>
                {SECTION_TITLES[sec.key]}
              </h4>
              <div className="space-y-3 text-xs">
                {sec.items.map((item) => (
                  <div key={item.id} className="p-3 bg-slate-50 rounded border border-slate-200 space-y-2">
                    <label className={`flex items-start space-x-3 ${isDone ? "opacity-70" : "cursor-pointer"}`}>
                      <input
                        data-testid={`checklist-item-${item.question_code.replace(".", "_")}`}
                        type="checkbox" className="mt-0.5 rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                        checked={!!item.is_checked} disabled={isDone}
                        onChange={(e) => toggleItem(item, e.target.checked)}
                      />
                      <span className="font-bold text-slate-800">{item.question_code} {item.question_text}</span>
                    </label>
                    {!isDone && (
                      <input
                        data-testid={`checklist-note-${item.question_code.replace(".", "_")}`}
                        type="text" placeholder="Notering (valfritt)..." defaultValue={item.note || ""}
                        onBlur={(e) => saveNote(item, e.target.value)}
                        className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                      />
                    )}
                    {isDone && item.note && <p className="text-slate-500 italic pl-7">Notering: {item.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white p-5 rounded-lg border border-slate-200 space-y-4">
            <h4 className="font-bold text-sm text-slate-800 border-b border-slate-100 pb-2 flex items-center">
              <span className="bg-blue-600 text-white w-5 h-5 rounded-full inline-flex items-center justify-center text-xs mr-2">3</span>
              Sektion 3: Avvikelser & Rapportering i Uppdraget
            </h4>
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
              <div>
                <h5 className="font-bold text-xs text-red-900">Har du identifierat någon kvalitetsbrist eller avvikelse under arbetet?</h5>
                <p className="text-xs text-red-700 mt-0.5">Rapporterade brister matas direkt in i Modul 4 för årlig utvärdering.</p>
              </div>
              <button
                data-testid="checklist-report-deviation-btn"
                onClick={() => onReportDeviation(active.id)}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow flex items-center whitespace-nowrap ml-4"
              >
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />Rapportera Avvikelse
              </button>
            </div>
          </div>

          <div className="bg-white p-5 rounded-lg border border-slate-200 space-y-3" data-testid="attachments-section">
            <h4 className="font-bold text-sm text-slate-800 border-b border-slate-100 pb-2 flex items-center">
              <Paperclip className="w-4 h-4 text-blue-600 mr-2" />Bilagor & Underlag
            </h4>
            {!isDone && (
              <div
                data-testid="upload-dropzone"
                className="p-5 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg text-center"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); uploadFile(e.dataTransfer.files?.[0]); }}
              >
                <Upload className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <h4 className="text-xs font-bold text-slate-700">Ladda upp underlag/bilaga till checklistan</h4>
                <p className="text-[11px] text-slate-500 mb-3">Dra & släpp signerat uppdragsavtal, KYC-legitimering eller avstämningsfil här.</p>
                <input ref={fileInputRef} type="file" className="hidden" data-testid="upload-input" onChange={(e) => uploadFile(e.target.files?.[0])} />
                <button data-testid="upload-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded shadow hover:bg-slate-700 disabled:opacity-60">
                  {uploading ? "Laddar upp..." : "Välj fil från datorn"}
                </button>
              </div>
            )}
            <div className="space-y-1.5" data-testid="attachment-list">
              {attachments.map((a) => (
                <div key={a.id} className="flex justify-between items-center text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
                  <span className="font-medium text-slate-700 truncate mr-3">{a.filename} <span className="text-slate-400">({Math.round((a.file_size_bytes || 0) / 1024)} kB)</span></span>
                  <button data-testid={`attachment-download-${a.id}`} onClick={() => downloadFile(a)} className="text-blue-600 hover:text-blue-800 font-semibold flex items-center whitespace-nowrap">
                    <Download className="w-3.5 h-3.5 mr-1" />Ladda ner
                  </button>
                </div>
              ))}
              {attachments.length === 0 && <p className="text-[11px] text-slate-400">Inga bilagor uppladdade ännu.</p>}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            {isDone ? (
              <div className="flex items-center text-emerald-700 text-sm font-bold" data-testid="checklist-signed-banner">
                <Lock className="w-4 h-4 mr-2" />
                Signerad & slutförd {active.completed_at ? new Date(active.completed_at).toLocaleString("sv-SE") : ""}
              </div>
            ) : isSupervisor ? (
              <button
                data-testid="checklist-sign-btn" onClick={sign} disabled={signing}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-extrabold px-6 py-2.5 rounded-lg shadow-md flex items-center"
              >
                <FileSignature className="w-4 h-4 mr-2" />
                {signing ? "SIGNERAR..." : "SIGNERA OCH SLUTFÖR UPPDRAG"}
              </button>
            ) : (
              <div className="flex items-center text-slate-500 text-xs italic" data-testid="checklist-sign-locked">
                <Lock className="w-3.5 h-3.5 mr-1.5" />Endast ansvarig Supervisor kan signera och slutföra uppdraget.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
